import { Users, TrendingUp, Truck, DollarSign, Receipt, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { cn } from '../lib/cn';
import { useAdminMetrics, useAdminHealth, formatCents, formatNumber } from '../api/hooks';
import type { AdminComponentHealth } from '../api/types';

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  subtitleType?: 'positive' | 'negative' | 'neutral';
  icon: React.ComponentType<{ size?: number; className?: string }>;
  loading?: boolean;
}

function StatCard({ title, value, subtitle, subtitleType = 'neutral', icon: Icon, loading }: StatCardProps) {
  if (loading) {
    return (
      <Card padding="md" hover={false}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-32 mb-1" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="w-10 h-10 rounded-lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card padding="md" hover={false}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-body-sm text-text-secondary">
            {title}
          </p>
          <p className="text-page-title text-text mt-1">
            {value}
          </p>
          {subtitle && (
            <p
              className={cn(
                'text-caption mt-1',
                subtitleType === 'positive' && 'text-profit',
                subtitleType === 'negative' && 'text-loss',
                subtitleType === 'neutral' && 'text-text-muted'
              )}
            >
              {subtitle}
            </p>
          )}
        </div>
        <div className="w-10 h-10 rounded-lg bg-action-container flex items-center justify-center">
          <Icon size={20} className="text-action" />
        </div>
      </div>
    </Card>
  );
}

function HealthStatusIcon({ status }: { status: AdminComponentHealth['status'] }) {
  switch (status) {
    case 'healthy':
      return <CheckCircle size={16} className="text-profit" />;
    case 'degraded':
      return <AlertCircle size={16} className="text-warning" />;
    case 'unhealthy':
      return <XCircle size={16} className="text-loss" />;
  }
}

function HealthStatusBadge({ status }: { status: AdminComponentHealth['status'] }) {
  return (
    <span
      className={cn(
        'badge',
        status === 'healthy' && 'badge-active',
        status === 'degraded' && 'badge-pending',
        status === 'unhealthy' && 'badge-suspended'
      )}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

/**
 * Admin dashboard overview page.
 * Shows key platform metrics and activity summary.
 */
export function OverviewPage() {
  const { data: metrics, isLoading: metricsLoading, error: metricsError } = useAdminMetrics();
  const { data: health, isLoading: healthLoading } = useAdminHealth();

  return (
    <>
      <Header title="Overview" description="Platform metrics and activity" />

      <div className="p-6">
        {/* Error State */}
        {metricsError && (
          <Card padding="md" hover={false} className="mb-6 border-loss/50">
            <div className="flex items-center gap-3 text-loss">
              <AlertCircle size={20} />
              <span className="text-body-sm">
                Failed to load metrics. Please check your connection and try again.
              </span>
            </div>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Users"
            value={metrics ? formatNumber(metrics.total_users) : '0'}
            subtitle={metrics ? `+${metrics.new_users_week} this week` : undefined}
            subtitleType="positive"
            icon={Users}
            loading={metricsLoading}
          />
          <StatCard
            title="Active Users (30d)"
            value={metrics ? formatNumber(metrics.active_users_30_days) : '0'}
            subtitle={metrics ? `${Math.round((metrics.active_users_30_days / Math.max(metrics.total_users, 1)) * 100)}% of total` : undefined}
            subtitleType="neutral"
            icon={TrendingUp}
            loading={metricsLoading}
          />
          <StatCard
            title="Total Loads"
            value={metrics ? formatNumber(metrics.total_loads) : '0'}
            subtitle={metrics ? `${metrics.loads_in_progress} in progress` : undefined}
            subtitleType="neutral"
            icon={Truck}
            loading={metricsLoading}
          />
          <StatCard
            title="Total Revenue"
            value={metrics ? formatCents(metrics.total_revenue_cents) : '$0'}
            subtitle={metrics ? `${formatCents(metrics.outstanding_invoice_cents)} outstanding` : undefined}
            subtitleType={metrics && metrics.overdue_invoice_cents > 0 ? 'negative' : 'neutral'}
            icon={DollarSign}
            loading={metricsLoading}
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard
            title="Receipts Pending"
            value={metrics ? formatNumber(metrics.receipts_pending) : '0'}
            subtitle={metrics ? `${metrics.receipts_processed} processed` : undefined}
            subtitleType="neutral"
            icon={Receipt}
            loading={metricsLoading}
          />
          <StatCard
            title="Invoices Overdue"
            value={metrics ? formatNumber(metrics.invoices_overdue) : '0'}
            subtitle={metrics ? formatCents(metrics.overdue_invoice_cents) : undefined}
            subtitleType={metrics && metrics.invoices_overdue > 0 ? 'negative' : 'neutral'}
            icon={AlertCircle}
            loading={metricsLoading}
          />
          <StatCard
            title="Invoices Paid (7d)"
            value={metrics ? formatNumber(metrics.invoices_paid_week) : '0'}
            subtitle={metrics ? `${metrics.loads_completed_week} loads completed` : undefined}
            subtitleType="positive"
            icon={CheckCircle}
            loading={metricsLoading}
          />
        </div>

        {/* Platform Health */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card padding="md" hover={false}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-card-title text-text">
                Platform Health
              </h2>
              {health && <HealthStatusBadge status={health.status} />}
            </div>

            {healthLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center justify-between">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                ))}
              </div>
            ) : health?.components && health.components.length > 0 ? (
              <div className="space-y-3">
                {health.components.map((component) => (
                  <div key={component.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HealthStatusIcon status={component.status} />
                      <span className="text-body-sm text-text-secondary capitalize">
                        {component.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-caption text-text-muted">
                        {component.latency_ms}ms
                      </span>
                      <HealthStatusBadge status={component.status} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-body-sm text-text-secondary text-center py-4">
                No health data available
              </div>
            )}
          </Card>

          <Card padding="md" hover={false}>
            <h2 className="text-card-title text-text mb-4">
              Quick Stats
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-body-sm text-text-secondary">
                  New Users Today
                </span>
                <span className="text-body-sm text-text font-medium">
                  {metrics?.new_users_today ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-body-sm text-text-secondary">
                  Total Expenses
                </span>
                <span className="text-body-sm text-text font-medium">
                  {metrics ? formatCents(metrics.total_expenses_cents) : '$0'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-body-sm text-text-secondary">
                  Failed Receipts
                </span>
                <span className={cn(
                  'text-body-sm font-medium',
                  metrics && metrics.receipts_failed > 0 ? 'text-loss' : 'text-text'
                )}>
                  {metrics?.receipts_failed ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-body-sm text-text-secondary">
                  Data Updated
                </span>
                <span className="text-body-sm text-text-muted">
                  {metrics?.generated_at ? new Date(metrics.generated_at).toLocaleTimeString() : 'N/A'}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
